import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../lib/firebase'
import { Vendor, VendorStatus, VendorCategory } from '../types/vendor'

const VENDORS_COLLECTION = 'vendors'

/**
 * Get all vendors for a user
 */
export async function getVendors(userId: string): Promise<Vendor[]> {
  const vendorsRef = collection(db, 'users', userId, VENDORS_COLLECTION)
  const q = query(vendorsRef, orderBy('lastEmailAt', 'desc'))
  const snapshot = await getDocs(q)

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    firstEmailAt: doc.data().firstEmailAt?.toDate(),
    lastEmailAt: doc.data().lastEmailAt?.toDate(),
    pauseUntil: doc.data().pauseUntil?.toDate(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  })) as Vendor[]
}

/**
 * Get vendors by status
 */
export async function getVendorsByStatus(userId: string, status: VendorStatus): Promise<Vendor[]> {
  const vendorsRef = collection(db, 'users', userId, VENDORS_COLLECTION)
  const q = query(vendorsRef, where('status', '==', status), orderBy('lastEmailAt', 'desc'))
  const snapshot = await getDocs(q)

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    firstEmailAt: doc.data().firstEmailAt?.toDate(),
    lastEmailAt: doc.data().lastEmailAt?.toDate(),
    pauseUntil: doc.data().pauseUntil?.toDate(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  })) as Vendor[]
}

/**
 * Get a single vendor by ID
 */
export async function getVendor(userId: string, vendorId: string): Promise<Vendor | null> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  const snapshot = await getDoc(vendorRef)

  if (!snapshot.exists()) return null

  return {
    id: snapshot.id,
    ...snapshot.data(),
    firstEmailAt: snapshot.data().firstEmailAt?.toDate(),
    lastEmailAt: snapshot.data().lastEmailAt?.toDate(),
    pauseUntil: snapshot.data().pauseUntil?.toDate(),
    createdAt: snapshot.data().createdAt?.toDate(),
    updatedAt: snapshot.data().updatedAt?.toDate()
  } as Vendor
}

/**
 * Find vendor by domain
 */
export async function findVendorByDomain(userId: string, domain: string): Promise<Vendor | null> {
  const vendorsRef = collection(db, 'users', userId, VENDORS_COLLECTION)
  const q = query(vendorsRef, where('domain', '==', domain))
  const snapshot = await getDocs(q)

  if (snapshot.empty) return null

  const doc = snapshot.docs[0]
  return {
    id: doc.id,
    ...doc.data(),
    firstEmailAt: doc.data().firstEmailAt?.toDate(),
    lastEmailAt: doc.data().lastEmailAt?.toDate(),
    pauseUntil: doc.data().pauseUntil?.toDate(),
    createdAt: doc.data().createdAt?.toDate(),
    updatedAt: doc.data().updatedAt?.toDate()
  } as Vendor
}

/**
 * Create or update a vendor
 */
export async function upsertVendor(
  userId: string,
  vendorData: Partial<Vendor> & { domain: string; name: string }
): Promise<Vendor> {
  // Check if vendor exists
  const existing = await findVendorByDomain(userId, vendorData.domain)

  if (existing) {
    // Update existing vendor
    const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, existing.id!)
    const updates = {
      ...vendorData,
      lastEmailAt: serverTimestamp(),
      totalEmailsReceived: (existing.totalEmailsReceived || 0) + 1,
      updatedAt: serverTimestamp()
    }
    await updateDoc(vendorRef, updates)
    return { ...existing, ...updates } as Vendor
  } else {
    // Create new vendor
    const vendorsRef = collection(db, 'users', userId, VENDORS_COLLECTION)
    const newVendorRef = doc(vendorsRef)

    // Build vendor object, excluding undefined values (Firebase doesn't allow undefined)
    const newVendor: Record<string, unknown> = {
      userId,
      name: vendorData.name,
      domain: vendorData.domain,
      category: vendorData.category || 'other',
      senderEmails: vendorData.senderEmails || [],
      firstEmailAt: serverTimestamp(),
      lastEmailAt: serverTimestamp(),
      totalEmailsReceived: 1,
      emailsOpened: 0,
      emailsClicked: 0,
      frequency: 'sporadic',
      promotionCount: 0,
      updateCount: 0,
      transactionalCount: 0,
      status: 'active',
      isVip: false,
      isHuman: vendorData.isHuman || false,
      digestOnly: false,
      unsubscribeMethod: vendorData.unsubscribeMethod || 'none',
      isEnriched: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    // Only add optional fields if they have values
    if (vendorData.logoUrl) {
      newVendor.logoUrl = vendorData.logoUrl
    }
    if (vendorData.unsubscribeLink) {
      newVendor.unsubscribeLink = vendorData.unsubscribeLink
    }

    await setDoc(newVendorRef, newVendor)
    return { id: newVendorRef.id, ...newVendor } as Vendor
  }
}

/**
 * Update vendor status
 */
export async function updateVendorStatus(
  userId: string,
  vendorId: string,
  status: VendorStatus,
  pauseDays?: number
): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  const updates: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp()
  }

  if (status === 'paused' && pauseDays) {
    const pauseUntil = new Date()
    pauseUntil.setDate(pauseUntil.getDate() + pauseDays)
    updates.pauseUntil = Timestamp.fromDate(pauseUntil)
  } else {
    updates.pauseUntil = null
  }

  await updateDoc(vendorRef, updates)
}

/**
 * Toggle vendor VIP status
 */
export async function toggleVendorVip(userId: string, vendorId: string, isVip: boolean): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    isVip,
    updatedAt: serverTimestamp()
  })
}

/**
 * Toggle digest only mode
 */
export async function toggleVendorDigest(userId: string, vendorId: string, digestOnly: boolean): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    digestOnly,
    updatedAt: serverTimestamp()
  })
}

/**
 * Mark a vendor as human (not a company/vendor)
 * This will dismiss all associated promotions and updates
 */
export async function markVendorAsHuman(userId: string, vendorId: string): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    isHuman: true,
    updatedAt: serverTimestamp()
  })

  // Dismiss all promotions from this vendor
  const promotionsRef = collection(db, 'users', userId, 'promotions')
  const promoQuery = query(promotionsRef, where('vendorId', '==', vendorId))
  const promoSnapshot = await getDocs(promoQuery)

  for (const promoDoc of promoSnapshot.docs) {
    await updateDoc(promoDoc.ref, { isDismissed: true })
  }

  // Dismiss all updates from this vendor
  const updatesRef = collection(db, 'users', userId, 'updates')
  const updateQuery = query(updatesRef, where('vendorId', '==', vendorId))
  const updateSnapshot = await getDocs(updateQuery)

  for (const updateDocSnapshot of updateSnapshot.docs) {
    await updateDoc(updateDocSnapshot.ref, { isDismissed: true })
  }
}

/**
 * Unmark a vendor as human (revert to being a vendor)
 */
export async function unmarkVendorAsHuman(userId: string, vendorId: string): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    isHuman: false,
    updatedAt: serverTimestamp()
  })
}

/**
 * Mark a sender as human or vendor by their email address.
 * Creates a new vendor record if one doesn't exist.
 * Returns the vendor ID.
 */
export async function setSenderHumanStatus(
  userId: string,
  email: string,
  fromName: string,
  isHuman: boolean
): Promise<string> {
  const domain = extractDomain(email)
  if (!domain) {
    throw new Error('Invalid email address')
  }

  // Try to find existing vendor by domain
  let vendor = await findVendorByDomain(userId, domain)

  if (!vendor) {
    // Create new vendor
    const name = extractVendorName(email, fromName)
    vendor = await upsertVendor(userId, {
      domain,
      name,
      senderEmails: [email.toLowerCase()],
      isHuman
    })
  }

  // Update human status
  if (isHuman) {
    await markVendorAsHuman(userId, vendor.id!)
  } else {
    await unmarkVendorAsHuman(userId, vendor.id!)
  }

  return vendor.id!
}

/**
 * Set auto-delete setting for a vendor
 */
export async function setVendorAutoDelete(
  userId: string,
  vendorId: string,
  days: number  // 0 = disabled, 30 = delete after 30 days
): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    autoDeleteAfterDays: days,
    updatedAt: serverTimestamp()
  })
}

/**
 * Set auto-delete for ALL vendors (batch operation)
 * Only applies to non-human vendors
 */
export async function setBatchAutoDelete(
  userId: string,
  days: number  // 0 = disabled, 7/14/30 = delete after X days
): Promise<number> {
  const vendors = await getVendors(userId)
  let updated = 0

  for (const vendor of vendors) {
    // Only apply to non-human vendors
    if (!vendor.isHuman && vendor.id) {
      const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendor.id)
      await updateDoc(vendorRef, {
        autoDeleteAfterDays: days,
        updatedAt: serverTimestamp()
      })
      updated++
    }
  }

  return updated
}

/**
 * Update vendor category
 */
export async function updateVendorCategory(
  userId: string,
  vendorId: string,
  category: VendorCategory
): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    category,
    updatedAt: serverTimestamp()
  })
}

/**
 * Delete a vendor
 */
export async function deleteVendor(userId: string, vendorId: string): Promise<void> {
  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await deleteDoc(vendorRef)
}

/**
 * Increment email opened count
 */
export async function incrementVendorOpened(userId: string, vendorId: string): Promise<void> {
  const vendor = await getVendor(userId, vendorId)
  if (!vendor) return

  const vendorRef = doc(db, 'users', userId, VENDORS_COLLECTION, vendorId)
  await updateDoc(vendorRef, {
    emailsOpened: (vendor.emailsOpened || 0) + 1,
    updatedAt: serverTimestamp()
  })
}

/**
 * Get vendor statistics
 */
export async function getVendorStats(userId: string): Promise<{
  total: number
  active: number
  muted: number
  highFrequency: number
  neverOpened: number
}> {
  const vendors = await getVendors(userId)

  return {
    total: vendors.length,
    active: vendors.filter(v => v.status === 'active').length,
    muted: vendors.filter(v => v.status === 'muted' || v.status === 'blocked').length,
    highFrequency: vendors.filter(v => v.frequency === 'daily').length,
    neverOpened: vendors.filter(v => v.emailsOpened === 0 && v.totalEmailsReceived > 2).length
  }
}

/**
 * Extract domain from email address
 */
export function extractDomain(email: string): string {
  const match = email.match(/@([^>]+)/)
  return match ? match[1].toLowerCase() : ''
}

/**
 * Extract vendor name from email/domain
 */
export function extractVendorName(email: string, fromName?: string): string {
  // If we have a from name that's not just an email, use it
  if (fromName && !fromName.includes('@') && fromName.trim().length > 0) {
    // Clean up common patterns
    return fromName
      .replace(/\s*(Newsletter|Updates|News|Notifications?|Team|Support|Info|No-?Reply)\s*/gi, '')
      .trim() || fromName.trim()
  }

  // Extract from domain
  const domain = extractDomain(email)
  if (!domain) return email

  // Get the main part of the domain (before TLD)
  const parts = domain.split('.')
  const mainPart = parts.length > 2 ? parts[parts.length - 2] : parts[0]

  // Capitalize first letter
  return mainPart.charAt(0).toUpperCase() + mainPart.slice(1)
}
